import {
  BillingEntityOptionSchema,
  BillingEntitySettingsSchema,
  type AppConfig,
  type BillingEntityOption,
  type BillingEntitySettings,
  type CreateBillingEntity,
  type UpdateBillingEntitySettings,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Settings API. Reads use the composed public endpoints (GET /website/settings,
 * GET /config); writes use the admin bulk-upsert PATCHes, which take raw
 * AppConfigEntry key/value pairs.
 */

export interface PublicWebsiteSettings {
  logoUrl: string | null;
  logoAlt: string;
  copyright: string;
  social: { platform: string; href: string }[];
  contact: {
    phone: string | null;
    email: string | null;
    address: string | null;
    whatsapp: string | null;
  };
  brand: Record<string, string>;
  /** Force-download URL for the latest company profile PDF, or null if unset. */
  companyProfilePdfUrl: string | null;
}

export type WebsiteEntry = { key: string; value: string | number | boolean | null };
export type AppConfigEntryInput = { key: string; value: unknown };

export const getWebsiteSettings = async (): Promise<PublicWebsiteSettings> =>
  (await apiClient.get<PublicWebsiteSettings>('/website/settings')).data;

export const updateWebsiteSettings = async (entries: WebsiteEntry[]): Promise<void> => {
  await apiClient.patch('/admin/website/settings', { entries });
};

export const getAppConfig = async (): Promise<AppConfig> =>
  (await apiClient.get<AppConfig>('/config')).data;

export const updateAppConfig = async (entries: AppConfigEntryInput[]): Promise<void> => {
  await apiClient.patch('/admin/config', { entries });
};

/** The invoice seller identity (billing entity) shown on every invoice. */
export const getBillingEntity = async (): Promise<BillingEntitySettings> => {
  const { data } = await apiClient.get<unknown>('/settings/billing-entity');
  return BillingEntitySettingsSchema.parse(data);
};

/** Update the billing entity (ADMIN only). */
export const updateBillingEntity = async (
  body: UpdateBillingEntitySettings,
): Promise<BillingEntitySettings> => {
  const { data } = await apiClient.put<unknown>('/settings/billing-entity', body);
  return BillingEntitySettingsSchema.parse(data);
};

/**
 * Multi-account GST support. The invoice form and the company settings tab both
 * list every billing entity (GST account); settings additionally reads a single
 * entity, creates one, updates one by id and re-points the default.
 */

/** List all billing entities (GST accounts). */
export const listBillingEntities = async (): Promise<BillingEntityOption[]> => {
  const { data } = await apiClient.get<unknown>('/settings/billing-entities');
  return BillingEntityOptionSchema.array().parse(data);
};

/** Read one billing entity's full settings. */
export const getBillingEntityById = async (id: string): Promise<BillingEntitySettings> => {
  const { data } = await apiClient.get<unknown>(`/settings/billing-entities/${id}`);
  return BillingEntitySettingsSchema.parse(data);
};

/** Create a new billing entity (ADMIN only). */
export const createBillingEntity = async (
  body: CreateBillingEntity,
): Promise<BillingEntitySettings> => {
  const { data } = await apiClient.post<unknown>('/settings/billing-entities', body);
  return BillingEntitySettingsSchema.parse(data);
};

/** Update a billing entity by id (ADMIN only). */
export const updateBillingEntityById = async (
  id: string,
  body: UpdateBillingEntitySettings,
): Promise<BillingEntitySettings> => {
  const { data } = await apiClient.put<unknown>(`/settings/billing-entities/${id}`, body);
  return BillingEntitySettingsSchema.parse(data);
};

/** Make a billing entity the default (ADMIN only). Returns the updated list. */
export const setDefaultBillingEntity = async (id: string): Promise<BillingEntityOption[]> => {
  const { data } = await apiClient.post<unknown>(`/settings/billing-entities/${id}/default`);
  return BillingEntityOptionSchema.array().parse(data);
};
